import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { api, formatErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { InvoiceFields, invoicePayload } from "@/components/InvoiceFields";

export default function BulkDoneDialog({ ids, onOpenChange, onDone }) {
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [invoice, setInvoice] = useState({ billable: null, amount: "" });
  const [busy, setBusy] = useState(false);
  const open = ids.length > 0;

  useEffect(() => {
    if (open) { setDate(format(new Date(), "yyyy-MM-dd")); setNotes(""); setInvoice({ billable: null, amount: "" }); }
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/compliance/bulk-done", { obligation_ids: ids, payment_date: date, notes: notes || null, ...invoicePayload(invoice) });
      if (data.failed.length) toast.error(`${data.failed.length} could not be marked: ${data.failed[0].error}`);
      if (data.done) toast.success(`${data.done} filing(s) marked done`);
      onDone();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="bulk-done-dialog">
        <DialogHeader>
          <DialogTitle>Mark {ids.length} filing{ids.length === 1 ? "" : "s"} done</DialogTitle>
          <DialogDescription>The same date, note and billing choice are saved for every selected filing. Each one rolls to its next period.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Filed on</Label>
            <Input data-testid="bulk-done-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 mono" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Note (optional)</Label>
            <Textarea data-testid="bulk-done-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" placeholder="e.g. ARN numbers shared on email" />
          </div>
          <InvoiceFields value={invoice} onChange={setInvoice} testid="bulk-done-invoice" />
          {invoice.billable && <p className="text-[11px] text-slate-500" data-testid="bulk-done-fee-hint">Leave the fee blank to use each filing's preset fee (client override → standard fee). A typed amount is used for every selected filing.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-done-cancel">Cancel</Button>
          <Button data-testid="bulk-done-submit" disabled={busy || !date} onClick={submit} className="bg-blue-700 hover:bg-blue-800 text-white">
            {busy ? "Saving…" : `Mark ${ids.length} done`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
